import { useEffect } from 'react'

import type { PlaylistOption } from '../types/api.types'
import { isPlayablePlaylist } from '../utils/playlists'

/**
 * Auto-select the default hold music playlist when playlists load.
 * Selects: is_default flag -> name starting with "default" -> first in list.
 *
 * Only considers playable (non-empty) playlists so it never silently
 * pre-selects a playlist with no audio tracks. If every playlist is empty,
 * nothing is selected and the user must add tracks first.
 *
 * Only runs when `enabled` is true — callers gate this on create-mode so it
 * never clobbers a loaded value (including a user's explicit empty selection)
 * in edit mode, even if the playlists query refetches.
 */
export const useAutoSelectDefaultPlaylist = (
  playlists: PlaylistOption[],
  setPlaylistId: (updater: (prev: string) => string) => void,
  enabled: boolean,
) => {
  useEffect(() => {
    if (!enabled) return
    const playable = playlists.filter(isPlayablePlaylist)
    if (playable.length === 0) return
    setPlaylistId((prev) => {
      if (prev !== '') return prev
      const defaultPlaylist =
        playable.find((playlist) => playlist.is_default) ??
        playable.find((playlist) => /^default/i.test(playlist.name))
      return defaultPlaylist
        ? String(defaultPlaylist.id)
        : String(playable[0].id)
    })
  }, [enabled, playlists, setPlaylistId])
}
